import { initializeApp } from "firebase/app";
import { IS_NATIVE } from "./capacitor";
import { 
  getAuth, 
  GoogleAuthProvider, 
  signInWithPopup,
  signInWithRedirect,
  getRedirectResult,
  signOut, 
  onAuthStateChanged 
} from "firebase/auth";
import {
  getFirestore,
  doc,
  setDoc,
  getDoc,
  updateDoc,
  collection,
  addDoc,
  getDocs,
  deleteDoc,
  query,
  where,
  orderBy,
  increment,
  serverTimestamp
} from "firebase/firestore";
import { getFunctions } from "firebase/functions";

const firebaseConfig = {
  apiKey: process.env.REACT_APP_FIREBASE_API_KEY,
  authDomain: process.env.REACT_APP_FIREBASE_AUTH_DOMAIN,
  projectId: process.env.REACT_APP_FIREBASE_PROJECT_ID,
  storageBucket: process.env.REACT_APP_FIREBASE_STORAGE_BUCKET,
  messagingSenderId: process.env.REACT_APP_FIREBASE_MESSAGING_SENDER_ID,
  appId: process.env.REACT_APP_FIREBASE_APP_ID,
  measurementId: process.env.REACT_APP_FIREBASE_MEASUREMENT_ID
};

const app = initializeApp(firebaseConfig);

export const auth = getAuth(app);
export const db = getFirestore(app);
export const functions = getFunctions(app);
export const googleProvider = new GoogleAuthProvider();
export { onAuthStateChanged };

// Inside src/utils/firebase.js

/** Complete a Google redirect only in the native shell. */
export const completeGoogleRedirectIfNeeded = () =>
  IS_NATIVE ? getRedirectResult(auth) : Promise.resolve(null);

export const signInWithGoogle = async (addToast) => {
  try {
    // Browsers use a popup so sign-in is immediate and keeps the user on the
    // current page. The native Capacitor shell uses a redirect instead.
    if (IS_NATIVE) {
      await signInWithRedirect(auth, googleProvider);
      return null;
    }

    const result = await signInWithPopup(auth, googleProvider);
    return result.user;
  } catch (error) {
    if (error.code === 'auth/cancelled-popup-request' || error.code === 'auth/popup-closed-by-user') {
      console.warn("Google sign-in was cancelled.");
      return null;
    }

    if (error.code === 'auth/popup-blocked' || (error.message && error.message.includes('Cross-Origin-Opener-Policy'))) {
      console.warn("Popup blocked by COOP policy, switching to redirect...");
      await signInWithRedirect(auth, googleProvider);
      return null;
    }

    console.error("Google sign-in failed", error);
    if (addToast) addToast("Login Error: " + error.message, 'error');
    throw error;
  }
};

export const logoutUser = () => signOut(auth);

export const savePlaylistToProfile = async (userId, playlistName, playlistUrl, previewLogos = []) => {
  try {
    if (!userId) {
      throw new Error('NOT_AUTHENTICATED');
    }

    if (!playlistUrl) {
      throw new Error('PLAYLIST_URL_REQUIRED');
    }

    // Helpful debug: show current auth context if available
    try {
      console.log('savePlaylistToProfile called by user:', auth?.currentUser?.uid || userId);
    } catch (x) {
      // ignore logging failures
    }

    const userPlaylistsCol = collection(db, "users", userId, "playlists");
    const userPlaylistsSnap = await getDocs(userPlaylistsCol);
    
    const isPro = await checkUserProStatus(userId);

    if (!isPro && userPlaylistsSnap.docs.length >= 1) {
      throw new Error("LIMIT_REACHED");
    }

    const newPlaylist = {
      name: playlistName,
      playlistUrl: playlistUrl, // ✅ CRITICAL: persist the M3U source URL
      sourceType: playlistUrl === 'Local File' ? 'file' : 'url',
      previewLogos: previewLogos,
      schemaVersion: 2,
      createdAt: new Date(),
      updatedAt: new Date()
    };

    const docRef = await addDoc(userPlaylistsCol, newPlaylist);
    // Return the created playlist (id + data) so callers can update UI optimistically
    return { id: docRef.id, ...newPlaylist };
    
  } catch (e) {
    console.error("Error saving playlist: ", e);
    // Normalize permission errors for upstream handling
    if (e.code === 'permission-denied' || /permission/i.test(e.message)) {
      const err = new Error('PERMISSION_DENIED');
      err.code = 'permission-denied';
      throw err;
    }
    throw e;
  }
};

export const getUserPlaylists = async (userId) => {
  try {
    if (!userId) return [];

    console.debug(`getUserPlaylists: reading users/${userId}/playlists`);

    const playlistsCol = collection(db, "users", userId, "playlists");
    const playlistsSnap = await getDocs(playlistsCol);

    const playlists = playlistsSnap.docs.map(d => ({
      id: d.id,
      ...d.data()
    }));

    console.debug(
      `getUserPlaylists: fetched ${playlists.length} playlists`,
      playlists.map(p => p.id)
    );

    return playlists;
  } catch (e) {
    console.error("Error fetching playlists:", e);

    // Bubble up permission issues explicitly
    if (e.code === 'permission-denied' || /permission/i.test(e.message)) {
      const err = new Error('PERMISSION_DENIED');
      err.code = 'permission-denied';
      throw err;
    }

    return [];
  }
};

export const deletePlaylistFromProfile = async (uid, playlistId) => {
  if (!uid || !playlistId) {
    throw new Error('Missing user id or playlist id');
  }

  const playlistRef = doc(db, 'users', uid, 'playlists', playlistId);
  await deleteDoc(playlistRef);
};

export const renamePlaylistInProfile = async (uid, playlistId, newName) => {
  if (!uid || !playlistId || !newName) throw new Error('Missing required params');
  const ref = doc(db, 'users', uid, 'playlists', playlistId);
  await updateDoc(ref, { name: newName, updatedAt: new Date() });
};

export const renameFavoriteChannel = async (uid, favoriteId, newName) => {
  if (!uid || !favoriteId || !newName) throw new Error('Missing required params');
  const ref = doc(db, 'users', uid, 'favorites', favoriteId);
  await updateDoc(ref, { name: newName });
};

export const addFavoriteChannel = async (userId, channelObj) => {
  try {
    const favsCol = collection(db, "users", userId, "favorites");
    await addDoc(favsCol, channelObj);
  } catch (e) {
    console.error("Error adding favorite: ", e);
  }
};

export const removeFavoriteChannel = async (userId, channel) => {
  try {
    if (!channel || !channel.url) {
      throw new Error("Invalid channel object provided.");
    }

    const favsCol = collection(db, "users", userId, "favorites");
    const q = query(favsCol, where("url", "==", channel.url));
    const querySnapshot = await getDocs(q);

    if (querySnapshot.empty) {
      console.warn("Favorite channel not found for URL:", channel.url);
      return; 
    }

    const deletePromises = [];
    querySnapshot.forEach((doc) => {
      deletePromises.push(deleteDoc(doc.ref));
    });
    
    await Promise.all(deletePromises);

  } catch (e) {
    console.error("Error removing favorite: ", e);
  }
};

export const getUserFavorites = async (userId) => {
  try {
    // First, try to read from the `users/{userId}/favorites` subcollection (preferred format)
    try {
      const favsCol = collection(db, "users", userId, "favorites");
      const favsSnap = await getDocs(favsCol);
      const fromSubcollection = favsSnap.docs.map(doc => ({ ...doc.data(), id: doc.id }));

      if (fromSubcollection.length > 0) return fromSubcollection;
    } catch (subErr) {
      // If we hit permission issues for subcollection reads, fall back to reading the user document
      if (subErr.code === 'permission-denied' || /permission/i.test(subErr.message)) {
        console.warn('Cannot read favorites subcollection due to Firestore rules; falling back to user document.', subErr);
      } else {
        throw subErr;
      }
    }

    // Fallback: attempt to read a legacy `favorites` array from the user document
    try {
      const userRef = doc(db, 'users', userId);
      const userSnap = await getDoc(userRef);
      if (userSnap.exists()) {
        const data = userSnap.data();
        if (Array.isArray(data.favorites) && data.favorites.length > 0) {
          const legacyFavs = data.favorites.map((f, idx) => ({ id: f.id || `legacy-${idx}`, name: f.name || f.title || 'Favorite', url: f.url || f.streamUrl || '', logo: f.logo || f.thumb || '' }));
          return legacyFavs;
        }
      }
    } catch (inner) {
      console.warn('Could not read legacy favorites from user doc:', inner);
    }

    return [];
  } catch (e) {
    console.error("Error fetching favorites: ", e);
    if (e.code === 'permission-denied' || /permission/i.test(e.message)) {
      const err = new Error('PERMISSION_DENIED');
      err.code = 'permission-denied';
      throw err;
    }
    return [];
  }
};


const normalizePlanType = (planRaw) => {
  if (!planRaw || typeof planRaw !== 'string') return null;
  const plan = planRaw.toLowerCase();
  if (plan.includes('life')) return 'lifetime';
  if (plan.includes('year') || plan.includes('annual')) return 'annual';
  if (plan.includes('month')) return 'monthly';
  return plan;
};

const parseExpiryFromUserData = (data) => {
  const rawExpiry =
    data.expiryDate ||
    data.premiumExpiresAt ||
    data.subscriptionEndsAt ||
    data.expiresAt ||
    null;

  if (!rawExpiry) return null;
  const parsed = rawExpiry.toDate ? rawExpiry.toDate() : new Date(rawExpiry);
  if (Number.isNaN(parsed.getTime())) return null;
  return parsed;
};

export const checkUserProStatus = async (uid) => {
  try {
    const userRef = doc(db, "users", uid);
    const docSnap = await getDoc(userRef);

    if (docSnap.exists()) {
      const data = docSnap.data();
      
      const normalizedPlan =
        normalizePlanType(data.plan) ||
        normalizePlanType(data.subscriptionPlan);

      const isTimedPlan = normalizedPlan === 'monthly' || normalizedPlan === 'annual';
      const expiry = parseExpiryFromUserData(data);
      const now = new Date();

      const hasAnyProFlag = Boolean(data.isPro || data.isPremium || data.pro);

      if (hasAnyProFlag) {
        if (isTimedPlan && expiry && now > expiry) {
          console.log("Subscription expired. Downgrading...");
          await updateDoc(userRef, { isPro: false, isPremium: false });
          return false;
        }

        if (!data.isPro) {
          await setDoc(
            userRef,
            {
              isPro: true,
              plan: normalizedPlan || data.plan || null,
              expiryDate: expiry ? expiry.toISOString() : data.expiryDate || null
            },
            { merge: true }
          );
        }

        return true;
      }

      if (isTimedPlan && expiry && now <= expiry) {
        await setDoc(
          userRef,
          {
            isPro: true,
            plan: normalizedPlan,
            expiryDate: expiry.toISOString()
          },
          { merge: true }
        );
        return true;
      }

      if (normalizedPlan === 'lifetime') {
        await setDoc(
          userRef,
          {
            isPro: true,
            plan: 'lifetime',
            expiryDate: null
          },
          { merge: true }
        );
        return true;
      }
    }
    return false;
  } catch (e) {
    console.error("Error checking pro status:", e);
    return false;
  }
};

export const getWatchTimeToday = async (uid) => {
  try {
    const today = new Date().toISOString().split('T')[0];
    const ref = doc(db, "users", uid, "watchStats", today);
    const snap = await getDoc(ref);
    return snap.exists() ? (snap.data().secondsWatched || 0) : 0;
  } catch (e) {
    console.warn("Failed to fetch server watch time:", e);
    return 0;
  }
};

export const syncWatchTimeToServer = async (uid, deltaSeconds) => {
  if (!uid || deltaSeconds <= 0) return;
  try {
    const today = new Date().toISOString().split('T')[0];
    const ref = doc(db, "users", uid, "watchStats", today);
    await setDoc(ref, {
      secondsWatched: increment(deltaSeconds),
      date: today,
      updatedAt: serverTimestamp()
    }, { merge: true });
  } catch (e) {
    console.warn("Failed to sync watch time to server:", e);
  }
};

export const shouldShowExpiryPrompt = async (uid) => {
  try {
    if (!uid) return false;

    const userRef = doc(db, "users", uid);
    const docSnap = await getDoc(userRef);
    if (!docSnap.exists()) return false;

    const data = docSnap.data();
    const normalizedPlan =
      normalizePlanType(data.plan) ||
      normalizePlanType(data.subscriptionPlan);
    const isTimedPlan = normalizedPlan === 'monthly' || normalizedPlan === 'annual';
    if (!isTimedPlan) return false;

    const expiry = parseExpiryFromUserData(data);
    if (!expiry) return false;

    const hasAnyProFlag = Boolean(data.isPro || data.isPremium || data.pro);
    return !hasAnyProFlag && new Date() > expiry;
  } catch (e) {
    console.error("Error checking expiry prompt status:", e);
    return false;
  }
};

export const saveWatchStateToCloud = async (uid, watchData) => {
  if (!uid || !auth.currentUser || auth.currentUser.isAnonymous || !watchData?.url) return;
  try {
    const ref = doc(db, "users", uid, "watchState", "current");
    await setDoc(ref, {
      ...watchData,
      updatedAt: serverTimestamp()
    }, { merge: true });
  } catch (e) {
    // Silent catch for permissions/guest users
  }
};

export const getWatchStateFromCloud = async (uid) => {
  if (!uid || !auth.currentUser || auth.currentUser.isAnonymous) return null;
  try {
    const ref = doc(db, "users", uid, "watchState", "current");
    const snap = await getDoc(ref);
    return snap.exists() ? snap.data() : null;
  } catch (e) {
    return null;
  }
};


